import { oc } from "@orpc/contract";
import { z } from "zod";

// ============================================================================
// List Missing Vendors
// ============================================================================

export const VendorFollowupListInputSchema = z.object({
  year: z.number().int().min(2000).max(2100),
});

const MissingVendorSchema = z.object({
  supplierId: z.string().uuid(),
  supplierName: z.string(),
  supplierStatus: z.enum(["active", "inactive", "on_hold"]),
  contacts: z.array(
    z.object({
      id: z.string().uuid(),
      name: z.string(),
      email: z.string().nullable(),
    }),
  ),
  agreementNames: z.array(z.string()),
  /** YYYY-MM strings for each month with no transaction data */
  missingMonths: z.array(z.string()),
  lastFollowup: z
    .object({
      sentAt: z.coerce.date(),
      subject: z.string(),
    })
    .nullable(),
  followupCount: z.number().int(),
  /** If set, vendor is in cooldown and cannot be emailed until this date */
  cooldownUntil: z.coerce.date().nullable(),
  /** Days between followup emails for this vendor (supplier override > org default > 30) */
  cadenceDays: z.number().int(),
});

const CompleteVendorSchema = z.object({
  supplierId: z.string().uuid(),
  supplierName: z.string(),
  agreementNames: z.array(z.string()),
});

const SentFollowupSchema = z.object({
  id: z.string().uuid(),
  supplierId: z.string().uuid(),
  supplierName: z.string().nullable(),
  to: z.string(),
  subject: z.string(),
  sentAt: z.coerce.date(),
  metadata: z
    .object({
      missingMonths: z.array(z.string()).optional(),
    })
    .nullable()
    .optional(),
});

const SupplierContactEntrySchema = z.object({
  supplierId: z.string().uuid(),
  supplierName: z.string(),
  /** Per-supplier cadence override, or null if using org default */
  cadenceOverrideDays: z.number().int().nullable(),
  contacts: z.array(
    z.object({
      id: z.string().uuid(),
      name: z.string(),
      email: z.string().nullable(),
      isTagged: z.boolean(),
    }),
  ),
});

const InboxEmailSchema = z.object({
  id: z.string(),
  supplierId: z.string().uuid().nullable(),
  supplierName: z.string().nullable(),
  from: z.string().nullable(),
  subject: z.string().nullable(),
  textContent: z.string().nullable(),
  receivedAt: z.coerce.date(),
  readAt: z.coerce.date().nullable(),
  /**
   * Whether the reply's sender address is a known contact of the displayed
   * supplier's company. False when the supplier was resolved from the email
   * thread but the sender isn't one of that supplier's contacts (e.g. a
   * forwarded reply, a shared mailbox, or a cross-thread mismatch) — the UI
   * flags these so a wrong supplier name is never shown with false confidence.
   */
  senderVerified: z.boolean(),
});

export const VendorFollowupListOutputSchema = z.object({
  vendors: z.array(MissingVendorSchema),
  completeVendors: z.array(CompleteVendorSchema),
  sentFollowups: z.array(SentFollowupSchema),
  inboxEmails: z.array(InboxEmailSchema),
  supplierContacts: z.array(SupplierContactEntrySchema),
  followupMailboxUpn: z.string().nullable(),
  followupTemplateId: z.string().uuid().nullable(),
  followupContactTagOptionId: z.string().uuid().nullable(),
  /** Org-level default cadence in days (30 if not configured) */
  defaultCadenceDays: z.number().int(),
});

export type VendorFollowupListInput = z.infer<
  typeof VendorFollowupListInputSchema
>;
export type VendorFollowupListOutput = z.infer<
  typeof VendorFollowupListOutputSchema
>;
export type MissingVendor = z.infer<typeof MissingVendorSchema>;

// ============================================================================
// Send Followup Emails
// ============================================================================

export const SendFollowupInputSchema = z.object({
  vendors: z
    .array(
      z.object({
        supplierId: z.string().uuid(),
        missingMonths: z.array(z.string()),
      }),
    )
    .min(1),
  /** If provided, overrides the saved template subject (must contain {{variable}} placeholders). */
  subjectOverride: z.string().optional(),
  /** If provided, overrides the saved template HTML body (must contain {{variable}} placeholders). */
  htmlContentOverride: z.string().optional(),
  /** Optional CC recipients (email addresses) applied to every followup email in this batch. */
  cc: z.array(z.string().email()).optional(),
  /** Optional BCC recipients (email addresses) applied to every followup email in this batch. */
  bcc: z.array(z.string().email()).optional(),
});

const SendFollowupResultSchema = z.object({
  supplierId: z.string().uuid(),
  success: z.boolean(),
  error: z.string().optional(),
  outboundEmailId: z.string().uuid().optional(),
});

export const SendFollowupOutputSchema = z.object({
  results: z.array(SendFollowupResultSchema),
});

export type SendFollowupInput = z.infer<typeof SendFollowupInputSchema>;
export type SendFollowupOutput = z.infer<typeof SendFollowupOutputSchema>;

// ============================================================================
// Toggle Followup Contact Tag
// ============================================================================

export const ToggleFollowupTagInputSchema = z.object({
  contactId: z.string().uuid(),
  tagged: z.boolean(),
});

export const ToggleFollowupTagOutputSchema = z.object({
  success: z.boolean(),
});

// ============================================================================
// Set Followup Cadence
// ============================================================================

export const SetCadenceInputSchema = z.object({
  supplierId: z.string().uuid(),
  /** Number of days between followups, or null to clear override and use org default */
  cadenceDays: z.number().int().min(1).nullable(),
});

export const SetCadenceOutputSchema = z.object({
  success: z.boolean(),
});

// ============================================================================
// Draft Reply (AI)
// ============================================================================

export const DraftReplyInputSchema = z.object({
  inboundEmailId: z.string().uuid(),
});

export const DraftReplyOutputSchema = z.object({
  replyNeeded: z.boolean(),
  /** HTML draft reply, or null when replyNeeded is false */
  draftHtml: z.string().nullable(),
  /** AI reasoning (shown to user when no reply is needed) */
  reasoning: z.string(),
});

// ============================================================================
// Reply to Inbox Email
// ============================================================================

export const ReplyToInboxEmailInputSchema = z.object({
  inboundEmailId: z.string().uuid(),
  htmlContent: z.string().min(1),
  isAiGenerated: z.boolean().optional(),
});

export const ReplyToInboxEmailOutputSchema = z.object({
  outboundEmailId: z.string().uuid(),
  success: z.boolean(),
});

// ============================================================================
// Mark Inbox Email Read
// ============================================================================

export const MarkInboxReadInputSchema = z.object({
  inboundEmailId: z.string().uuid(),
  read: z.boolean().optional().default(true),
});

export const MarkInboxReadOutputSchema = z.object({
  success: z.boolean(),
});

// ============================================================================
// Contract Definitions
// ============================================================================

export const vendorFollowupContracts = {
  list: oc
    .input(VendorFollowupListInputSchema)
    .output(VendorFollowupListOutputSchema)
    .route({ method: "GET" }),

  sendFollowup: oc
    .input(SendFollowupInputSchema)
    .output(SendFollowupOutputSchema)
    .route({ method: "POST" }),

  toggleFollowupTag: oc
    .input(ToggleFollowupTagInputSchema)
    .output(ToggleFollowupTagOutputSchema)
    .route({ method: "POST" }),

  setCadence: oc
    .input(SetCadenceInputSchema)
    .output(SetCadenceOutputSchema)
    .route({ method: "POST" }),

  draftReply: oc
    .input(DraftReplyInputSchema)
    .output(DraftReplyOutputSchema)
    .route({ method: "POST" }),

  replyToInboxEmail: oc
    .input(ReplyToInboxEmailInputSchema)
    .output(ReplyToInboxEmailOutputSchema)
    .route({ method: "POST" }),

  markInboxRead: oc
    .input(MarkInboxReadInputSchema)
    .output(MarkInboxReadOutputSchema)
    .route({ method: "POST" }),
} as const;
